import React, { useCallback } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  ExpandedState,
  SortingState,
  getPaginationRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  Row,
} from '@tanstack/react-table';
import { motion, AnimatePresence } from 'framer-motion';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

import { cn } from '@/lib/utils';

// Skeleton component for loading state
const Skeleton = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('animate-original-pulse rounded-md bg-muted', className)} {...props} />
);

// Framer Motion Accordion Variants for bouncy accordion effect
const accordionVariants = {
  initial: {
    height: 0,
  },
  animate: {
    height: 'auto',
    transition: {
      height: {
        type: 'spring',
        stiffness: 450, // increase to make it more stiff (less bouncy), therefore increasing the speed of the animation
        damping: 20, // increase to make it more dampened and slow
      },
    },
  },
  exit: {
    height: 0,
    transition: {
      height: {
        duration: 0.1,
        ease: 'linear',
      },
    },
  },
};

export type ExtendedColumnDef<TData, TValue = unknown> = ColumnDef<TData, TValue> & {
  expandedContent?: (row: Row<TData>) => React.ReactNode;
};

export interface DataTableProps<TData, TValue> extends React.HTMLAttributes<HTMLDivElement> {
  columns: ExtendedColumnDef<TData, TValue>[];
  data: TData[];
  getRowCanExpand?: (row: Row<TData>) => boolean;
  className?: string;
  expandeRowClassName?: string;
  isLoading?: boolean;
  skeletonRowCount?: number;
}

export function DataTable<TData extends Record<string, any> & { id?: string | number }, TValue>({
  columns,
  data,
  getRowCanExpand = () => true,
  className,
  expandeRowClassName,
  isLoading = false,
  skeletonRowCount = 10,
  ...props
}: DataTableProps<TData, TValue>) {
  const [expanded, setExpanded] = React.useState<ExpandedState>({});
  const [sorting, setSorting] = React.useState<SortingState>([]);

  // Reset expanded state when data changes. Collapse all rows when data changes.
  React.useEffect(() => {
    setExpanded({});
  }, [data]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      expanded,
      sorting,
    },
    onExpandedChange: setExpanded,
    onSortingChange: setSorting,
    getRowCanExpand: columns.some((col) => col.expandedContent) ? getRowCanExpand : undefined,
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Render the skeleton rows for loading state
  const renderSkeletonRows = useCallback(() => {
    return Array.from({ length: skeletonRowCount }).map((_, index) => (
      <TableRow key={`skeleton-${index}`}>
        {columns.map((column, colIndex) => (
          <TableCell
            key={`skeleton-cell-${index}-${colIndex}`}
            className="first:pl-4 last:pr-4 py-4"
            style={{ width: column.size }}
          >
            <Skeleton className="h-4 w-full" />
          </TableCell>
        ))}
      </TableRow>
    ));
  }, [skeletonRowCount, columns]);

  // Render the table rows
  const renderTableRows = useCallback(() => {
    return table.getRowModel().rows.map((row) => (
      <React.Fragment key={row.original.id || row.id}>
        <TableRow data-state={row.getIsSelected() && 'selected'} className={cn(row.getIsExpanded() && 'border-b-0')}>
          {row.getVisibleCells().map((cell) => (
            <TableCell key={cell.id} className="first:pl-4 last:pr-4 py-4" style={{ width: cell.column.getSize() }}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          ))}
        </TableRow>
        <AnimatePresence initial={false}>
          {row.getIsExpanded() &&
            (() => {
              // Find the first column with expandedContent
              const expandedColumn = columns.find((col) => col.expandedContent);

              return expandedColumn?.expandedContent ? (
                <>
                  <TableRow aria-hidden role="presentation" className="border-none">
                    <TableCell colSpan={row.getVisibleCells().length} className="p-0 inset-0 h-[1px] w-full">
                      <div className="custom-dashed-border-b absolute inset-0 h-[1px]" />
                    </TableCell>
                  </TableRow>
                  <TableRow key={`${row.id}-expanded`}>
                    <TableCell
                      colSpan={row.getVisibleCells().length}
                      className={cn('first:pl-4 last:pr-4 py-4 overflow-hidden', expandeRowClassName)}
                    >
                      <motion.div variants={accordionVariants} initial="initial" animate="animate" exit="exit">
                        {expandedColumn.expandedContent(row)}
                      </motion.div>
                    </TableCell>
                  </TableRow>
                </>
              ) : null;
            })()}
        </AnimatePresence>
      </React.Fragment>
    ));
  }, [table, columns, expandeRowClassName]);

  const hasResults = table.getRowModel().rows?.length;

  return (
    <div className={cn('overflow-hidden rounded-md border border-border bg-card', className)} {...props}>
      <Table className="overflow-hidden">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className="space-x-4 first:pl-4 last:pr-4 py-4 text-current"
                  style={{ width: header.getSize() }}
                >
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isLoading ? (
            renderSkeletonRows()
          ) : hasResults ? (
            renderTableRows()
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
